/**
 * ActionCard — a proposal, and the human decision it is waiting for.
 *
 * The whole point of this component is that NOTHING HAS HAPPENED YET. It
 * shows what was found, what approving would create, and says plainly that
 * the thing is created disabled or as a draft. Approve calls the product's own
 * decide route; the card then shows what exists now and where to open it.
 *
 * Friction is tiered to consequence: low and medium risk approve with one
 * click, high risk requires a typed reason.
 */
import React, { lazy, Suspense, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import { besideClick } from "shared/components/records/recordsSurface";
import axiosInstance from "shared/services/api/axiosInstance";
import { useTeam } from "shared/context/teamContext";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import { RoutineEditor } from "shared/components/chat/ActionCardEditors";
import { AlertEditor, AlertPostApproval, alertCannotApprove } from "shared/components/chat/AlertCardEditor";

// The agent's graph as the editor draws it, read-only; loaded when an agent is proposed.
const FlowCanvas = lazy(() => import("features/agents/canvas/FlowCanvas"));

const KIND_LABEL = {
  prompt_edit: "Prompt change",
  create_routine: "New routine",
  create_alert: "New alert",
  create_agent: "New agent",
  create_dataset: "New dataset",
  dataset_source: "Dataset source",
  set_expected: "Set Expected",
  merge_issues: "Merge issues",
};
const SOURCE_CREATES = {
  add: "Approving adds the source and runs it once now.",
  change: "Approving changes the source. A new filter runs again over its window.",
  pause: "Approving pauses the source. Its rows stay.",
  resume: "Approving resumes the source.",
  remove: "Approving removes the source. The rows it added stay.",
  run: "Approving runs the source now.",
};
const CREATES = {
  prompt_edit: "Approving creates a draft version. It does not publish it.",
  create_routine: "Approving creates the routine switched off. You turn it on.",
  create_agent: "Approving creates a draft agent. You review it and publish it in the editor; nothing runs until then.",
  // The alert card carries its own on/off choice, so the sentence follows it.
  create_alert: (p) => (p?.enabled ? "Approving creates the alert and switches it on." : "Approving creates the alert switched off. You turn it on."),
  create_dataset: (p) => {
    const sources = Array.isArray(p?.sources) ? p.sources : [];
    if (!sources.length) return "Approving creates the empty dataset.";
    return `Approving creates the dataset and runs ${sources.length === 1 ? "its source" : "its sources"} once now.${sources.some((s) => s.live) ? " A live source keeps adding rows every 15 minutes." : ""}`;
  },
  dataset_source: (p) => SOURCE_CREATES[p?.action] || "Approving changes the source.",
  set_expected: "Approving writes these Expected values. Nothing else changes.",
  merge_issues: "Approving moves the first issue's evidence into the second and marks the first merged.",
};
const createsText = (type, payload) => (typeof CREATES[type] === "function" ? CREATES[type](payload) : CREATES[type]);
const TIER = {
  low: { label: "Low risk", color: theme.textSecondary },
  medium: { label: "Needs review", color: "var(--feedback-warning)" },
  high: { label: "High risk", color: "var(--feedback-error)" },
};

function DiffView({ diff }) {
  if (!diff) return null;
  return (
    <pre style={{ fontSize: 11, lineHeight: 1.5, overflowX: "auto", borderRadius: 8, padding: 10, marginTop: 8, maxHeight: 320, background: "var(--surface-sunken, rgba(0,0,0,0.2))" }}>
      {String(diff).split("\n").map((line, i) => {
        const added = line.startsWith("+");
        const removed = line.startsWith("-");
        return (
          <div key={i} style={{ color: added ? "var(--feedback-success)" : removed ? "var(--feedback-error)" : theme.textSecondary, whiteSpace: "pre-wrap" }}>{line}</div>
        );
      })}
    </pre>
  );
}

/** What the draft IS, per kind — the fields a reviewer reads before approving. */
function Draft({ item, teamId, onEdit }) {
  const p = item.payload || {};
  const row = (k, v) => (v == null || v === "" ? null : (
    <div key={k} style={{ display: "flex", gap: 8, fontSize: 12 }}>
      <span style={{ color: theme.textSecondary, minWidth: 92 }}>{k}</span>
      <span style={{ color: theme.textPrimary, wordBreak: "break-word" }}>{String(v)}</span>
    </div>
  ));
  if (item.type === "create_routine") {
    return <RoutineEditor payload={p} onChange={onEdit} />;
  }
  if (item.type === "create_alert") {
    return <AlertEditor payload={p} teamId={teamId} onChange={onEdit} />;
  }
  if (item.type === "create_agent") {
    const nodes = Array.isArray(p.nodes) ? p.nodes : [];
    const graph = p.definition && Array.isArray(p.definition.nodes) && p.definition.nodes.length ? p.definition : null;
    return (
      <div style={{ display: "grid", gap: 3, marginTop: 8 }}>
        {row("name", p.name)}{row("what it does", p.description)}{row("trigger", p.trigger?.name)}
        {graph ? (
          // The flow itself, as the editor will open it: trigger, steps and branches.
          // Positioned: the canvas fills its nearest positioned box, which was the whole page.
          <div className="relative mt-1 overflow-hidden rounded-md border border-[color:var(--border-default)]"
            style={{ height: Math.min(360, 140 + graph.nodes.length * 28) }}>
            <Suspense fallback={null}><FlowCanvas definition={graph} mode="run" readOnly /></Suspense>
          </div>
        ) : row("steps", nodes.filter((n) => n.name !== p.trigger?.name).map((n) => n.name).join(" > "))}
        {row("to connect", (p.missing || []).join(", "))}
      </div>
    );
  }
  if (item.type === "merge_issues") {
    return (
      <div style={{ display: "grid", gap: 3, marginTop: 8 }}>
        {row("merge", p.from ? `#${p.from}` : null)}{row("into", p.into ? `#${p.into}` : null)}{row("why", p.reason)}
      </div>
    );
  }
  if (item.type === "create_dataset") {
    const sources = Array.isArray(p.sources) ? p.sources : [];
    return (
      <div style={{ display: "grid", gap: 3, marginTop: 8 }}>
        {row("name", p.name)}{row("about", p.description)}
        {row("copies", sources.length
          ? sources.map((s) => `${s.describes}${s.matched_words ? ` (${s.matched_words})` : ""}`).join("; ")
          : "Nothing yet. Add rows by hand, by picking, or with a source later.")}
      </div>
    );
  }
  if (item.type === "dataset_source") {
    return (
      <div style={{ display: "grid", gap: 3, marginTop: 8 }}>
        {row("dataset", p.dataset_name)}
        {row(p.after ? "now" : "source", p.before)}
        {row(p.action === "add" ? "copies" : "after", p.after)}
      </div>
    );
  }
  if (item.type === "set_expected") {
    const rows = Array.isArray(p.rows) ? p.rows : [];
    const shown = rows.slice(0, 10);
    return (
      <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
        {row("dataset", p.dataset_name)}
        {shown.map((r) => (
          <div key={r.item_id} style={{ fontSize: 12, borderTop: `1px solid ${theme.border}`, paddingTop: 6 }}>
            {r.input && <div style={{ color: theme.textSecondary, wordBreak: "break-word" }}>{r.input}</div>}
            <div style={{ color: theme.textPrimary, wordBreak: "break-word" }}>
              {r.before ? <><span style={{ textDecoration: "line-through", color: theme.textSecondary }}>{r.before}</span>{" > "}</> : null}{r.after}
            </div>
          </div>
        ))}
        {rows.length > shown.length && <div style={{ fontSize: 12, color: theme.textSecondary }}>and {rows.length - shown.length} more</div>}
      </div>
    );
  }
  return null;
}

const DATASET_KINDS = new Set(["create_dataset", "dataset_source", "set_expected"]);

/** Where the created thing lives, once it exists. */
function openLink(item, teamSlug) {
  const ref = item.applied_ref || {};
  const base = `/${encodeURIComponent(teamSlug || "")}`;
  if (item.type === "prompt_edit") return { to: `${base}/prompts${ref.prompt_id ? `/${ref.prompt_id}` : ""}`, label: `Open the prompt${ref.version_no ? ` (draft v${ref.version_no})` : ""}` };
  if (item.type === "create_routine") return { to: `${base}/settings`, label: "Open settings" };
  if (item.type === "create_agent" && ref.workflow_id) return { to: `${base}/agents/${encodeURIComponent(ref.workflow_id)}`, label: "Open agent" };
  if (item.type === "merge_issues") return { to: `${base}/agent-analytics/issues`, label: "Open issues" };
  if (item.type === "create_alert") return { to: `${base}/alerts${ref.alert_id ? `/${encodeURIComponent(ref.alert_id)}` : ""}`, label: "Open the alert" };
  if (DATASET_KINDS.has(item.type)) {
    const id = ref.dataset_id || item.payload?.dataset_id;
    return { to: `${base}/datasets${id ? `/${encodeURIComponent(id)}` : ""}`, label: "Open the dataset" };
  }
  return null;
}

/** The one line an applied card says, before its link. */
function appliedText(type, ref) {
  if (type === "dataset_source") return "Done.";
  if (type === "merge_issues") return `Merged #${ref?.merged ?? ""} into #${ref?.into ?? ""}.`;
  if (type === "set_expected") return `Expected set on ${ref?.rows_set ?? 0} row${ref?.rows_set === 1 ? "" : "s"}.`;
  if (type === "create_dataset") return `Created${(ref?.sources || []).length ? "; its sources are running" : ""}.`;
  return null;
}

export default function ActionCard({ item }) {
  const { selectedTeam } = useTeam();
  const teamId = selectedTeam?.id ?? selectedTeam?._id ?? null;
  const teamSlug = selectedTeam?.slug || selectedTeam?.name || (teamId != null ? String(teamId) : "");
  const [state, setState] = useState(item);
  const [edits, setEdits] = useState({});
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);

  // A card in a reopened thread asks what became of its proposal.
  useEffect(() => {
    let cancelled = false;
    if (!item.proposal_id || !teamId) return undefined;
    axiosInstance.get(`/ask/proposals/${item.proposal_id}`, { params: { team_id: teamId } })
      .then(({ data }) => {
        if (cancelled || !data?.proposal) return;
        const p = data.proposal;
        setState((s) => ({ ...s, status: p.status === "open" ? "proposed" : p.status, applied_ref: p.applied_ref, error: p.error, reason: p.reason, payload: p.payload || s.payload }));
      })
      .catch(() => { /* the card keeps what the answer carried */ });
    return () => { cancelled = true; };
  }, [item.proposal_id, teamId]);

  const decide = async (action) => {
    if (!item.proposal_id) return;
    setBusy(true); setError(null);
    try {
      const { data } = await axiosInstance.post(
        `/ask/proposals/${item.proposal_id}/decide`,
        {
          action, reason: reason || undefined, team_id: teamId,
          // What the reviewer edited on the card — validated server-side the
          // way the product's own create route would validate it, before
          // anything is claimed. Never sent on dismiss.
          ...(action === "approve" && Object.keys(edits).length ? { payload: edits } : {}),
        },
        { params: { team_id: teamId } },
      );
      const p = data?.proposal || {};
      setState((s) => ({ ...s, status: p.status || (action === "approve" ? "applied" : "dismissed"), applied_ref: p.applied_ref, error: p.error }));
    } catch (e) {
      const code = e?.response?.status;
      if (code === 409) setState((s) => ({ ...s, status: "decided" }));
      else setError(e?.response?.data?.error || "Could not reach the server: try again.");
    } finally {
      setBusy(false);
    }
  };

  const tier = TIER[state.risk_tier] || TIER.medium;
  const decided = state.status && state.status !== "proposed";
  const needsReason = state.risk_tier === "high";
  const link = openLink(state, teamSlug);
  // The card's own edit can resolve the block: pick a provider and it clears.
  const mergedPayload = { ...state.payload, ...edits, config: { ...state.payload?.config, ...edits.config } };
  // An alert with nowhere to send to would quietly tell nobody.
  const cannotApprove = state.type === "create_alert" && alertCannotApprove(mergedPayload);
  const cannotApproveWhy = "Choose a Slack channel or a webhook first";

  return (
    <div className="trodo-action-card bg-surface" data-action-type={state.type} style={{ border: "1px solid var(--border-default)", borderRadius: 8, padding: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{KIND_LABEL[state.type] || String(state.type || "action").replace(/_/g, " ")}</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: theme.textPrimary, marginTop: 2 }}>{state.title}</div>
          {state.rationale && <div style={{ fontSize: 13, color: theme.textSecondary, marginTop: 4, lineHeight: 1.45 }}>{state.rationale}</div>}
        </div>
        <span style={{ flexShrink: 0, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", padding: "2px 6px", borderRadius: 4, border: `1px solid ${tier.color}`, color: tier.color }}>{tier.label}</span>
      </div>

      <Draft
        item={{ ...state, payload: { ...state.payload, ...edits } }}
        teamId={teamId}
        onEdit={decided ? undefined : (patch) => setEdits((e) => ({ ...e, ...patch }))}
      />

      {state.payload?.diff && (
        <button type="button" onClick={() => setOpen((o) => !o)} style={{ marginTop: 8, fontSize: 12, color: theme.primary, background: "none", border: "none", padding: 0, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
          {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}{open ? "Hide the change" : "Review the change"}
        </button>
      )}
      {open && <DiffView diff={state.payload.diff} />}

      <div style={{ fontSize: 11, color: theme.textSecondary, marginTop: 8 }}>
        {decided ? null : `Nothing has changed. ${createsText(state.type, mergedPayload) || "Approving creates a draft."}`}
      </div>

      {decided ? (
        <div style={{ fontSize: 12, marginTop: 6, color: theme.textSecondary }}>
          {state.status === "applied" && state.type !== "create_alert" && (
            <span>
              {appliedText(state.type, state.applied_ref) || (
                <>Created{state.type === "prompt_edit" && state.applied_ref?.version_no ? ` as draft v${state.applied_ref.version_no}` : ""}{state.type === "create_routine" ? ", switched off" : ""}{state.type === "create_agent" ? " as a draft. Review it and publish it from the editor" : ""}.</>
              )}
              {link && <> <Link to={link.to} onClick={besideClick(link.to)} style={{ color: theme.primary }}>{link.label}</Link></>}
            </span>
          )}
          {state.status === "applied" && state.type === "create_alert" && state.applied_ref?.alert_id && (
            <AlertPostApproval
              alertId={state.applied_ref.alert_id}
              enabled={state.applied_ref?.enabled}
              teamSlug={teamSlug}
              name={state.applied_ref?.name || mergedPayload.name}
            />
          )}
          {(state.status === "dismissed" || state.status === "rejected") && <span>Dismissed{state.reason ? `: ${state.reason}` : "."}</span>}
          {state.status === "failed" && <span style={{ color: "var(--feedback-error)" }}><AlertTriangle size={12} style={{ display: "inline", marginRight: 4 }} />Could not be created: {state.error}</span>}
          {state.status === "decided" && <span>Already decided elsewhere.</span>}
        </div>
      ) : (
        <>
          {needsReason && (
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why are you approving this? (required)"
              style={{ marginTop: 8, width: "100%", fontSize: 12, borderRadius: 8, border: `1px solid ${theme.border}`, padding: "6px 8px", background: "transparent", color: theme.textPrimary }} />
          )}
          {error && <div style={{ fontSize: 12, color: "var(--feedback-error)", marginTop: 6 }}>{error}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button type="button" disabled={busy || cannotApprove || (needsReason && !reason.trim())} onClick={() => decide("approve")}
              title={cannotApprove ? cannotApproveWhy : undefined}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 32, fontSize: 12, fontWeight: 500, borderRadius: 6, padding: "0 12px", border: "none", cursor: "pointer", background: theme.primary, color: "var(--text-on-brand)", opacity: busy || cannotApprove || (needsReason && !reason.trim()) ? 0.4 : 1 }}>
              <Check size={13} /> Approve
            </button>
            <button type="button" disabled={busy} onClick={() => decide("reject")}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 32, fontSize: 12, fontWeight: 500, borderRadius: 6, padding: "0 12px", border: "1px solid var(--border-default)", background: "transparent", color: theme.textSecondary, cursor: "pointer", opacity: busy ? 0.4 : 1 }}>
              <X size={13} /> Dismiss
            </button>
          </div>
        </>
      )}
    </div>
  );
}
